import React, { useRef, useState, useEffect } from "react";
import { Button, Input, Modal } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import lang from "../helper/langHelper";
import NormalEditor from "./NormalEditor";
import MultipleImageUpload from "./MultipleImageUpload";

// Per-option comments are saved inside one note as "Option 1: …" lines, one per option.
// Split that back out so each card can show what was said about it.
const splitOptionComments = (commentData, numbers) => {
  const count = numbers.length;
  const out = Array.from({ length: count }, () => []);
  (commentData || []).forEach((c) => {
    if (!c || c.isDraft || !c.comment) return;
    let cur = null;
    String(c.comment).split("\n").forEach((line) => {
      const m = line.match(/^\s*Option\s+(\d+)\s*:\s*(.*)$/i);
      if (m) {
        cur = numbers.indexOf(Number(m[1]));
        if (cur >= 0) out[cur].push({ name: c.commentUserName, internal: !!c.isInternal, type: c.type, time: c.time, text: m[2] });
        else cur = null;
      } else if (cur !== null && out[cur].length) {
        out[cur][out[cur].length - 1].text += "\n" + line;
      }
    });
  });
  return out;
};

const isVideo = (url = "") => /\.(mp4|mov|avi|webm|mkv|m4v)(\?|$)/i.test(url);
const isPdf = (url = "") => /\.pdf(\?|$)/i.test(url);

// Side-by-side workspace for a post's 2-3 caption options, in ONE scrolling
// area (half the width each for two, a third each for three):
//  - every option has the full post-editor toolbar, so it can be edited here
//  - every option has its own comment box underneath
//  - the footer carries the workflow buttons (Send / Approve / Request Changes…)
//    supplied by the parent via `actions`
//
//  mode="select": staff tick which options go out (at least one).
//  mode="choose": the client picks exactly one option.

const plainText = (html = "") =>
  String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const styles = `
.post-options-modal .ant-modal-content { border-radius: 20px; overflow: hidden; box-shadow: 0 30px 80px rgba(15,23,42,0.28); padding: 0; }
.post-options-modal .ant-modal-header { padding: 22px 32px 18px !important; border-bottom: 1px solid #eef0f4 !important; background: #fff !important; }
.post-options-modal .ant-modal-title { text-align: left !important; line-height: 1.3 !important; }
.post-options-modal .ant-modal-close { top: 10px; right: 12px; }
.post-options-modal .ant-modal-body { padding: 22px 32px 8px !important; background: linear-gradient(180deg, #f6f8fc 0%, #eef2f9 100%); }
.post-options-modal .ant-modal-footer { padding: 16px 32px 20px !important; border-top: 1px solid #eef0f4 !important; background: #fff !important; }

.poc-title-wrap { display: flex; flex-direction: column; gap: 4px; }
.poc-title { font-size: 20px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; }
.poc-subtitle { font-size: 13px; font-weight: 400; color: #64748b; }

.poc-scroll { max-height: 66vh; overflow-y: auto; padding: 4px 4px 18px; margin: 0 -4px; scrollbar-width: thin; }
.poc-row { display: flex; align-items: stretch; gap: 20px; }

.poc-card { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 18px rgba(15,23,42,0.05); transition: border-color .2s, box-shadow .2s; position: relative; }
.poc-card.is-picked { border-color: #3b82f6; box-shadow: 0 0 0 4px rgba(59,130,246,0.12), 0 12px 32px rgba(37,99,235,0.14); }
.poc-card::before { content: ""; height: 5px; background: #e2e8f0; transition: background .2s; }
.poc-card.is-picked::before { background: linear-gradient(90deg, #2563eb, #06b6d4); }

.poc-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px 20px; border-bottom: 1px solid #f1f5f9; background: #fff; }
.poc-card.is-picked .poc-head { background: linear-gradient(180deg, rgba(59,130,246,0.06), #fff); }
.poc-head-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
.poc-badge { width: 34px; height: 34px; flex: 0 0 34px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-weight: 700; font-size: 15px; color: #64748b; background: #f1f5f9; transition: all .2s; }
.poc-card.is-picked .poc-badge { color: #fff; background: linear-gradient(135deg, #2563eb, #06b6d4); box-shadow: 0 4px 12px rgba(37,99,235,0.35); }
.poc-approved { display: inline-flex; align-items: center; gap: 4px; margin-left: 8px; padding: 2px 9px; border-radius: 999px; background: #dcfce7; color: #166534; font-size: 11px; font-weight: 700; vertical-align: middle; }
.poc-client-approved { display: inline-flex; align-items: center; gap: 4px; margin-left: 8px; padding: 2px 9px; border-radius: 999px; background: #dbeafe; color: #1e40af; font-size: 11px; font-weight: 700; vertical-align: middle; }
.poc-history { padding: 10px 20px 0; }
.poc-history-title { font-size: 12px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: .4px; margin: 6px 0 8px; }
.poc-history-item { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 8px 12px; margin-bottom: 8px; font-size: 13px; line-height: 1.5; color: #1e293b; white-space: pre-wrap; }
.poc-history-meta { display: flex; align-items: center; gap: 8px; font-size: 11px; color: #64748b; margin-bottom: 2px; }
.poc-history-tag { padding: 0 7px; border-radius: 999px; background: #fef3c7; color: #92400e; font-weight: 700; }
.poc-head-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
.poc-toggle-changes.is-on { border-color: #dc2626; background: #fef2f2; color: #b91c1c; }
.poc-toggle-changes.is-on .poc-tick { background: #dc2626; border-color: #dc2626; color: #fff; }
.poc-name { font-size: 15px; font-weight: 700; color: #0f172a; line-height: 1.2; }
.poc-meta { font-size: 12px; color: #94a3b8; margin-top: 2px; }

.poc-toggle { display: inline-flex; align-items: center; gap: 8px; padding: 7px 14px 7px 10px; border-radius: 999px; border: 1.5px solid #dbe2ea; background: #fff; color: #475569; font-size: 13px; font-weight: 600; cursor: pointer; user-select: none; transition: all .2s; white-space: nowrap; }
.poc-toggle:hover { border-color: #93c5fd; color: #1d4ed8; }
.poc-toggle .poc-tick { width: 18px; height: 18px; border-radius: 50%; border: 1.5px solid #cbd5e1; display: inline-flex; align-items: center; justify-content: center; font-size: 10px; color: transparent; transition: all .2s; }
.poc-toggle.is-on { border-color: #2563eb; background: #eff6ff; color: #1d4ed8; }
.poc-toggle.is-on .poc-tick { background: #2563eb; border-color: #2563eb; color: #fff; }

.poc-body { flex: 1; padding: 18px 20px 6px; font-size: 16px; line-height: 1.5; color: #1e293b; word-break: break-word; }
.poc-body .rdw-editor-main { font-size: 16px; line-height: 1.5; color: #16233f; padding: 14px 16px; }
.poc-body .rdw-editor-main .public-DraftStyleDefault-block { margin: 0 !important; }
.poc-body .rdw-editor-main .public-DraftStyleDefault-block.post-empty-block { min-height: 1.5em; }
.poc-body .poc-readonly { font-size: 16px; line-height: 1.5; color: #16233f; white-space: pre-wrap; }
.poc-body .poc-readonly p, .poc-body .poc-readonly div { margin: 0; white-space: pre-wrap; }
.poc-body .poc-readonly p:empty, .poc-body .poc-readonly p:has(> br:only-child) { min-height: 1.5em; }
.poc-body .normal-editor-shell { border-radius: 12px; }
.poc-card.is-dim .poc-head, .poc-card.is-dim .poc-body { opacity: 0.55; }

.poc-media { padding: 6px 20px 18px; }
.poc-media-label { display: block; font-size: 12px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: .4px; margin: 6px 0 10px; }
.poc-media-grid { display: flex; flex-wrap: wrap; gap: 10px; }
.poc-media-grid img { width: 104px; height: 104px; object-fit: cover; border-radius: 8px; border: 1px solid #e2e8f0; background: #f8fafc; display: block; }
.poc-media-grid video { width: 220px; max-height: 130px; border-radius: 8px; border: 1px solid #e2e8f0; background: #000; display: block; }
.poc-media-doc { display: flex; align-items: center; gap: 10px; padding: 12px 14px; border: 1px solid #e2e8f0; border-radius: 12px; background: #f8fafc; font-size: 13px; color: #1d4ed8; word-break: break-all; }
.poc-media-empty { font-size: 13px; color: #94a3b8; padding: 4px 0 2px; }
.poc-comment { padding: 10px 20px 20px; border-top: 1px dashed #e2e8f0; margin-top: 8px; background: #fbfcfe; }
.poc-comment label { display: block; font-size: 12px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: .4px; margin: 10px 0 6px; }
.poc-comment textarea { border-radius: 10px !important; resize: vertical; }

.poc-foot { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
.poc-foot-left { font-size: 13px; color: #64748b; display: flex; align-items: center; gap: 4px; }
.poc-foot-left strong { color: #0f172a; }
.poc-foot-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; justify-content: flex-end; }
.post-options-modal .poc-foot-actions .ant-btn { height: 44px !important; min-height: 44px !important; min-width: 110px; padding: 0 22px !important; margin: 0 !important; border-radius: 10px !important; font-weight: 600; font-size: 14px; display: inline-flex !important; align-items: center !important; justify-content: center !important; line-height: 1 !important; box-shadow: none; transform: none !important; }
.post-options-modal .poc-foot-actions .ant-btn > span { line-height: 1; display: inline-block; }
.post-options-modal .poc-foot-actions .ant-btn-primary { box-shadow: 0 6px 16px rgba(15,23,42,0.18); }
.post-options-modal .poc-foot-actions .ant-btn-dangerous:not(.ant-btn-primary) { background: #fff; }

@media (max-width: 900px) { .poc-row { flex-direction: column; } }
`;

function PostOptionsCompareModal({
  open,
  options = [],
  mode = "select",
  selection = [],
  onSelectionChange,
  chosenIndex = null,
  onChosenChange,
  editable = false,
  onTextChange,
  comments = [],
  onCommentChange,
  media = [], // per option: [{ url }]
  approvedFlags = [], // per option: true when a reviewer approved it
  approvedBy = [], // per option: reviewer name
  toggleLabels = null, // { on, off } wording for the select-mode pill (default Choose / Chosen)
  clientApprovedFlags = [], // per option: true when the client approved it
  clientApprovedBy = [], // per option: client name
  commentData = [], // the post's comments as the viewer is allowed to see them
  singleSelect = false, // select mode: exactly one option can be ticked (e.g. Publish)
  numbers = null, // per card: the option number everyone uses (defaults to 1, 2, 3)
  changeFlags = null, // choose mode: per option, true when the client wants changes on it (any number of options)
  onChangeFlagToggle = null,
  showInternalTag = false, // staff only: tag internal comments
  onMediaChange,
  mediaConfig = null, // { fileType, btnName, maxCount } — null = this post type has no media
  commentLabel,
  commentPlaceholder,
  title,
  subtitle,
  actions = [],
  onCancel,
}) {
  const editorRefs = useRef([]);
  const selectedCount = selection.filter(Boolean).length;
  const optionNumbers = options.map((_, i) => (numbers && numbers[i]) || i + 1);
  const historyByOption = splitOptionComments(commentData, optionNumbers);
  const needsPick = mode === "choose" ? chosenIndex === null || chosenIndex === undefined : selectedCount === 0;

  const flushAll = () => editorRefs.current.forEach((r) => r && r.flush && r.flush());
  // The clicked action shows a spinner right away and the rest are locked until the
  // screen closes (the page closes it once the work has finished).
  const [pendingKey, setPendingKey] = useState(null);
  useEffect(() => {
    if (!open) setPendingKey(null);
  }, [open]);

  const toggle = (i, checked) => {
    const next = options.map((_, idx) => (singleSelect ? idx === i : idx === i ? checked : selection[idx] !== false));
    onSelectionChange && onSelectionChange(next);
  };

  const heading =
    title ||
    (mode === "choose" ? lang("Choose the option you want to publish") : lang("Review & send your options"));
  const sub =
    subtitle ||
    (mode === "choose"
      ? lang("Read the options side by side, edit if you like, leave a comment on any option, then decide.")
      : lang("Edit each option, add comments, tick the ones to include, then send."));

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      centered
      zIndex={3000}
      width="94vw"
      style={{ maxWidth: 1600 }}
      className="post-options-modal"
      destroyOnClose
      title={
        <div className="poc-title-wrap">
          <style>{styles}</style>
          <span className="poc-title">{heading}</span>
          <span className="poc-subtitle">{sub}</span>
        </div>
      }
      footer={
        <div className="poc-foot">
          <div className="poc-foot-left">
            {mode === "select" ? (
              <>
                <strong>{selectedCount}</strong> / {options.length} {lang("selected")}
                {options.length > 1 && !singleSelect && (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => onSelectionChange && onSelectionChange(options.map(() => selectedCount !== options.length))}
                  >
                    {selectedCount === options.length ? lang("Clear all") : lang("Select all")}
                  </Button>
                )}
              </>
            ) : (
              <>{chosenIndex !== null && chosenIndex !== undefined ? `${lang("Option")} ${chosenIndex + 1} ${lang("chosen")}` : lang("Pick one option to approve")}</>
            )}
          </div>
          <div className="poc-foot-actions">
            <Button onClick={onCancel} disabled={!!pendingKey}>{lang("Cancel")}</Button>
            {actions.map((a) => (
              <Button
                key={a.key}
                type={a.type || "default"}
                danger={!!a.danger}
                loading={!!a.loading || pendingKey === a.key}
                disabled={!!a.disabled || (a.needsPick && needsPick) || (!!pendingKey && pendingKey !== a.key)}
                onClick={() => {
                  if (pendingKey) return;
                  flushAll();
                  setPendingKey(a.key);
                  a.onClick && a.onClick();
                }}
              >
                {a.label}
              </Button>
            ))}
          </div>
        </div>
      }
    >
      {/* One scroll container wraps the whole row, and the row is as tall as
          its tallest option — every card's border wraps its full content and
          headers scroll with it. */}
      <div className="poc-scroll post-options-compare">
        <div className="poc-row">
          {options.map((o, i) => {
            const picked = mode === "choose" ? chosenIndex === i : selection[i] !== false;
            const text = plainText(o.text);
            const words = text ? text.split(" ").length : 0;
            return (
              <div
                key={i}
                className={`poc-card${picked ? " is-picked" : ""}${mode === "select" && !picked ? " is-dim" : ""}`}
              >
                <div className="poc-head">
                  <div className="poc-head-left">
                    <span className="poc-badge">{optionNumbers[i]}</span>
                    <div>
                      <div className="poc-name">
                        {lang("Option")} {optionNumbers[i]}
                        {clientApprovedFlags[i] && (
                          <span className="poc-client-approved" title={clientApprovedBy[i] ? `${lang("Approved by")} ${clientApprovedBy[i]}` : undefined}>
                            <CheckOutlined /> {lang("Approved by client")}
                          </span>
                        )}
                        {approvedFlags[i] && (
                          <span className="poc-approved" title={approvedBy[i] ? `${lang("Approved by")} ${approvedBy[i]}` : undefined}>
                            <CheckOutlined /> {lang("Approved")}
                          </span>
                        )}
                      </div>
                      <div className="poc-meta">
                        {words} {lang("words")} · {text.length} {lang("characters")}
                      </div>
                    </div>
                  </div>
                  <div className="poc-head-right">
                  {mode === "select" ? (
                    <span
                      role="checkbox"
                      aria-checked={picked}
                      tabIndex={0}
                      className={`poc-toggle${picked ? " is-on" : ""}`}
                      onClick={() => toggle(i, !picked)}
                      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggle(i, !picked); } }}
                    >
                      <span className="poc-tick"><CheckOutlined /></span>
                      {picked ? (toggleLabels?.on || lang("Chosen")) : (toggleLabels?.off || lang("Choose"))}
                    </span>
                  ) : (
                    <span
                      role="radio"
                      aria-checked={picked}
                      tabIndex={0}
                      className={`poc-toggle${picked ? " is-on" : ""}`}
                      onClick={() => onChosenChange && onChosenChange(i)}
                      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); onChosenChange && onChosenChange(i); } }}
                    >
                      <span className="poc-tick"><CheckOutlined /></span>
                      {picked ? lang("Chosen") : lang("Choose this")}
                    </span>
                  )}
                  {mode === "choose" && onChangeFlagToggle && (
                    <span
                      role="checkbox"
                      aria-checked={!!(changeFlags && changeFlags[i])}
                      tabIndex={0}
                      className={`poc-toggle poc-toggle-changes${changeFlags && changeFlags[i] ? " is-on" : ""}`}
                      onClick={() => onChangeFlagToggle(i, !(changeFlags && changeFlags[i]))}
                      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); onChangeFlagToggle(i, !(changeFlags && changeFlags[i])); } }}
                    >
                      <span className="poc-tick"><CheckOutlined /></span>
                      {lang("Needs changes")}
                    </span>
                  )}
                  </div>
                </div>

                <div className="poc-body post-options-compare-body">
                  {editable ? (
                    <NormalEditor
                      ref={(el) => { editorRefs.current[i] = el; }}
                      content={o.text}
                      onChange={(v) => onTextChange && onTextChange(i, v)}
                      toolbarPreset="linkedin"
                      placeholder={lang("Write this caption option...")}
                      minHeight={240}
                    />
                  ) : (
                    <div className="poc-readonly" dangerouslySetInnerHTML={{ __html: o.text }} />
                  )}
                </div>

                {(mediaConfig || (media[i] || []).length > 0) && (
                  <div className="poc-media">
                    <label className="poc-media-label">{lang("Media")}</label>
                    {editable && mediaConfig && onMediaChange ? (
                      <MultipleImageUpload
                        fileType={mediaConfig.fileType}
                        imageType="blog"
                        btnName={mediaConfig.btnName}
                        maxCount={mediaConfig.maxCount}
                        onChange={(files) => onMediaChange(i, files)}
                        data={media[i] || []}
                        isDisable={false}
                      />
                    ) : (media[i] || []).length > 0 ? (
                      <div className="poc-media-grid">
                        {(media[i] || []).map((f, k) =>
                          isVideo(f.url) ? (
                            <video key={k} controls src={f.url} />
                          ) : isPdf(f.url) ? (
                            <a key={k} className="poc-media-doc" href={f.url} target="_blank" rel="noreferrer">
                              {lang("PDF document")} — {String(f.url).split("/").pop()}
                            </a>
                          ) : (
                            <img key={k} src={f.url} alt={`${lang("Option")} ${optionNumbers[i]} ${lang("media")}`} />
                          )
                        )}
                      </div>
                    ) : (
                      <div className="poc-media-empty">{lang("No media on this option")}</div>
                    )}
                  </div>
                )}

                {historyByOption[i] && historyByOption[i].length > 0 && (
                  <div className="poc-history">
                    <div className="poc-history-title">{lang("Comments on")} {lang("Option")} {optionNumbers[i]}</div>
                    {historyByOption[i].map((h, k) => (
                      <div className="poc-history-item" key={k}>
                        <div className="poc-history-meta">
                          <b>{h.name || lang("Someone")}</b>
                          {showInternalTag && h.internal && <span className="poc-history-tag">{lang("Internal")}</span>}
                          {h.time && <span>{new Date(h.time).toLocaleString()}</span>}
                        </div>
                        {h.text}
                      </div>
                    ))}
                  </div>
                )}

                {onCommentChange && (
                  <div className="poc-comment">
                    <label>
                      {commentLabel || lang("Comment on")} {lang("Option")} {optionNumbers[i]}
                    </label>
                    <Input.TextArea
                      rows={3}
                      value={comments[i] || ""}
                      onChange={(e) => onCommentChange(i, e.target.value)}
                      placeholder={commentPlaceholder || lang("Add a comment about this option...")}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

export default PostOptionsCompareModal;
